import { Dayjs } from 'dayjs';

import { SystemId } from '@app/config/system-id';
import { SBaseDisplay, SDate } from '@summit/core';
import { MileageFormatter, SummitFormattedText } from '@summit/models';

import { AssetAssignment } from '../asset/asset-assignment';
import { EboObject } from '../base-classes/ebo-object';
import { MaintPlan } from '../maintenance/maint-plan';
import { MaintStatus } from '../maintenance/maint-status';
import { Company } from '../organization/company/company';
import { ExpenseType, ExpenseTypeMapping } from '../payroll/expense/expense-type.enum';
import { Hub } from '../places/hub/hub';
import { State } from '../places/state/state';
import { Route } from '../route/route';
import { AliasAssignment } from './alias-assignment';
import { TruckOdometerOffset } from './odometer/truck-odometer-offset';
import { TruckCabinType } from './truck-cabin-type.enum';
import { TruckExpense } from './truck-expense';
import { TruckFile } from './truck-file';
import { TruckSmokingPreference } from './truck-smoking-preference.enum';
import { TruckStatusHistory } from './truck-status-history';
import { TruckStatus } from './truck-status.enum';
import { TruckTransmission } from './truck-transmission.enum';
import { TruckType } from './truck-type.enum';
import { TruckValuation } from './valuation/truck-valuation';
import { VinDetails } from './vin-details';

SDate;

export class Truck extends EboObject implements SBaseDisplay {
  hubID: number;
  hub: Hub;
  companyID: string;
  company: Company;
  name: string;
  type: TruckType;
  sub4TruckID: string;
  sub4Truck: Truck;
  routeID?: string;
  route?: Route;
  status: TruckStatus;
  vin: string;
  year: number;
  cabinType: TruckCabinType;
  smokingPreference: TruckSmokingPreference;
  transmission: TruckTransmission;
  licensePlate: string;
  lpStateID?: string;
  lpState?: State;
  points?: number;
  availability?: number;
  assignedRouteID: string;
  assignedRoute: Route;
  currentStatus: TruckStatusHistory;
  statusHistory: TruckStatusHistory[];
  aliasAssignments: AliasAssignment[];
  assignedAssets: AssetAssignment[];
  expenses: TruckExpense[];
  valuations: TruckValuation[];
  currentValuation: TruckValuation;
  lastQuarterValuation: TruckValuation;
  files: TruckFile[];
  odometerOffsets: TruckOdometerOffset[];

  vinDetailId: string;
  vinDetail: VinDetails;

  //Maint
  maintPlanId: string;
  maintPlan: MaintPlan;
  maintStatuses: MaintStatus[];
  tireSize: string;
  currentOdometer: number;

  //Hack for initial Truck create
  newStatusDate: Dayjs;
  aliasStartTime: Dayjs;
  aliasEndTime: Dayjs;
  newStatus: TruckStatus | string;
  notes: string;

  get referenceNumbers(): SummitFormattedText {
    const response = new SummitFormattedText();
    const now = SDate.new();
    const activeAliases = this.aliasAssignments.filter((entry) => {
      return entry.endTime === null || entry.endTime.isSameOrAfter(now);
    });
    const activeAssets = this.assignedAssets.filter((entry) => {
      return entry.endTime === null || entry.endTime.isSameOrAfter(now);
    });
    if (activeAliases && activeAliases.length > 0) {
      activeAliases.forEach((assignment) => {
        if (assignment) {
          if (assignment.comment) response.AddKeyValues(assignment.alias?.toShortString(), assignment.comment);
          else response.AddLine(assignment.alias?.toShortString());
          if (assignment.alias?.comment && assignment.alias.comment !== assignment.comment) {
            response.AddLine(assignment.alias.comment);
          }
        }
      });
    }
    if (activeAssets && activeAssets.length > 0) {
      activeAssets.forEach((assignment) => {
        if (assignment.comment) response.AddKeyValues(assignment.asset?.toShortString(), assignment.comment);
        else response.AddLine(assignment.asset?.toShortString());
      });
    }
    return response;
  }

  get fedexNumber(): SummitFormattedText {
    const response = new SummitFormattedText();
    const now = SDate.new();
    const activeAliases = this.aliasAssignments.filter((entry) => {
      return entry.endTime === null || entry.endTime.isSameOrAfter(now);
    });
    if (activeAliases && activeAliases.length > 0) {
      activeAliases.forEach((assignment) => {
        if (assignment) {
          response.AddLine(assignment.alias?.name);
        }
      });
    }
    return response;
    // Enums
  }
  get settlementCompany(): SummitFormattedText {
    const response = new SummitFormattedText();
    const now = SDate.new();
    const activeAliases = this.aliasAssignments.filter((entry) => {
      return entry.endTime === null || entry.endTime.isSameOrAfter(now);
    });
    if (activeAliases && activeAliases.length > 0) {
      activeAliases.forEach((assignment) => {
        if (assignment) {
          response.AddLine(assignment.settlementCompany?.name);
        }
      });
    }
    return response;
    // Enums
  }
  get settlementCompanyEntity(): SummitFormattedText {
    const response = new SummitFormattedText();
    const now = SDate.new();
    const activeAliases = this.aliasAssignments.filter((entry) => {
      return entry.endTime === null || entry.endTime.isSameOrAfter(now);
    });
    if (activeAliases && activeAliases.length > 0) {
      activeAliases.forEach((assignment) => {
        if (assignment) {
          response.AddLine(assignment.settlementCompany?.fedExEntityId);
        }
      });
    }
    return response;
    // Enums
  }
  get settlementCompanyContract(): SummitFormattedText {
    const response = new SummitFormattedText();
    const now = SDate.new();
    const activeAliases = this.aliasAssignments.filter((entry) => {
      return entry.endTime === null || entry.endTime.isSameOrAfter(now);
    });
    if (activeAliases && activeAliases.length > 0) {
      activeAliases.forEach((assignment) => {
        if (assignment) {
          response.AddLine(assignment.settlementCompany?.fedExContractId);
        }
      });
    }
    return response;
    // Enums
  }
  get authorizedAgent(): SummitFormattedText {
    const response = new SummitFormattedText();
    const now = SDate.new();
    const activeAliases = this.aliasAssignments.filter((entry) => {
      return entry.endTime === null || entry.endTime.isSameOrAfter(now);
    });
    if (activeAliases && activeAliases.length > 0) {
      activeAliases.forEach((assignment) => {
        if (assignment) {
          response.AddLine(assignment.settlementCompany?.authorizedRepresentative?.name);
        }
      });
    }
    return response;
    // Enums
  }
  get authorizedAgentEmail(): SummitFormattedText {
    const response = new SummitFormattedText();
    const now = SDate.new();
    const activeAliases = this.aliasAssignments.filter((entry) => {
      return entry.endTime === null || entry.endTime.isSameOrAfter(now);
    });
    if (activeAliases && activeAliases.length > 0) {
      activeAliases.forEach((assignment) => {
        if (assignment) {
          response.AddLine(assignment.settlementCompany?.authorizedRepresentative?.email);
        }
      });
    }
    return response;
    // Enums
  }
  get authorizedAgentPhoneNumber(): SummitFormattedText {
    const response = new SummitFormattedText();
    const now = SDate.new();
    const activeAliases = this.aliasAssignments.filter((entry) => {
      return entry.endTime === null || entry.endTime.isSameOrAfter(now);
    });
    if (activeAliases && activeAliases.length > 0) {
      activeAliases.forEach((assignment) => {
        if (assignment) {
          response.AddLine(assignment.settlementCompany?.authorizedRepresentative?.phoneNumber);
        }
      });
    }
    return response;
    // Enums
  }
  get prepass(): SummitFormattedText {
    const response = new SummitFormattedText();
    const now = SDate.new();
    const prepassAssets = this.assignedAssets.filter(
      (entry) =>
        entry.asset?.companyID.toUpperCase() === SystemId.PrePassCompanyID &&
        (entry.endTime === null || entry.endTime.isSameOrAfter(now))
    );
    if (prepassAssets) {
      prepassAssets.forEach((assignment) => {
        response.AddLine(assignment.asset?.name);
      });
    }
    return response;
    // Enums
  }

  get odometerOffsetSummary(): SummitFormattedText {
    const response = new SummitFormattedText();
    if (this.odometerOffsets && this.odometerOffsets.length > 0) {
      this.odometerOffsets.forEach((entry) => {
        response.AddKeyValues(entry.effectiveDate.format('M/D/YY'), MileageFormatter(entry.actualValue));
        if (entry.odometerOffset)
          response.AddLine(`IVMR Offset: ${MileageFormatter(entry.odometerOffset)}`, ['flex', 'justify-end']);
      });
    }
    return response;
  }
  get drivers(): string[] {
    return null;
  }
  get omniTracs(): string[] {
    return null;
  }
  get lytx(): string[] {
    return null;
  }
  get siriusxm(): string[] {
    return null;
  }
  get make(): string {
    return this.vinDetail?.ncsaMake;
  }
  set make(value: string) {
    return;
  }
  get vinCabType(): string {
    return this.vinDetail?.cabType;
  }
  set vinCabType(value: string) {
    return;
  }
  get driveType(): string {
    return this.vinDetail?.driveType;
  }
  set driveType(value: string) {
    return;
  }
  get fuelType(): string {
    return this.vinDetail?.fuelType;
  }
  set fuelType(value: string) {
    return;
  }
  get engineManufacturer(): string {
    return this.vinDetail?.engineManufacturer;
  }
  set engineManufacturer(value: string) {
    return;
  }
  get trim(): string {
    return this.vinDetail?.trim;
  }
  set trim(value: string) {
    return;
  }
  get model(): string {
    return this.vinDetail?.model;
  }
  set model(value: string) {
    return;
  }
  get brakeSystemType(): string {
    return this.vinDetail?.brakeSystemType;
  }
  set brakeSystemType(value: string) {
    return;
  }

  get displayName(): string {
    return this.toShortString();
  }
  set displayName(value: string) {
    return;
  }

  get priceBase(): number {
    return this.currentValuation?.priceBase;
  }

  get valuationAsOf(): Dayjs {
    return this.currentValuation?.effectiveDate;
  }

  private _priceAdj: number;
  get valueAdj(): number {
    if (this._priceAdj === undefined) {
      this._priceAdj = this.currentValuation?.GetValueAdj(this.year, this.currentOdometer);
    }
    return this._priceAdj;
  }

  get estimatedValue(): number {
    return this.currentValuation?.GetEstimatedValue(this.year, this.currentOdometer);
  }

  get lastQuarterPriceBase(): number {
    return this.lastQuarterValuation?.GetEstimatedValue(this.year, this.currentOdometer);
  }

  private _daysTilNextService: number;
  get daysTilNextService(): number {
    if (this._daysTilNextService === undefined) {
      if (this.maintStatuses?.length > 0) {
        const daysRemaining = this.maintStatuses.map((m) => m.remainingDays).filter((d) => d || d === 0);
        if (daysRemaining.length > 0) {
          this._daysTilNextService = Math.min(...daysRemaining);
        }
      }
      this._daysTilNextService ??= null;
    }
    return this._daysTilNextService;
  }

  private _milesTilNextService: number;
  get milesTilNextService(): number {
    if (this._milesTilNextService === undefined) {
      //console.log('milesTilNextService', this.maintStatuses);
      if (this.maintStatuses?.length > 0) {
        const remainingMiles = this.maintStatuses.map((m) => m.remainingMiles).filter((d) => d || d === 0);
        if (remainingMiles.length > 0) {
          this._milesTilNextService = Math.min(...remainingMiles);
        }
      }
      this._milesTilNextService ??= null;
    }
    return this._milesTilNextService;
  }
  private _milesStatus: string;
  get milesStyle() {
    if (this._milesStatus === undefined) {
      if (this.maintStatuses.findIndex((m) => m.showDistanceAlarm()) > -1) {
        //console.log(`alarm ${this.name}`, this.maintStatuses);
        this._milesStatus = 'alarm';
      } else if (this.maintStatuses.findIndex((m) => m.showDistanceWarn()) > -1) {
        this._milesStatus = 'warn';
      } else if (this.maintStatuses.findIndex((m) => m.showDistanceFlag()) > -1) {
        this._milesStatus = 'flag';
      }
    }
    return this._milesStatus;
  }

  constructor(jsonData?: Partial<Truck>) {
    super(jsonData);
    if (jsonData) {
      if (jsonData.vinDetail) this.vinDetail = new VinDetails(jsonData.vinDetail);
      if (jsonData.hub) this.hub = new Hub(jsonData.hub);
      if (jsonData.company) this.company = new Company(jsonData.company);
      if (jsonData.assignedRoute) this.assignedRoute = new Route(jsonData.assignedRoute);
      if (jsonData.route) this.route = new Route(jsonData.route);
      if (jsonData.lpState) this.lpState = new State(jsonData.lpState);
      if (jsonData.sub4Truck) this.sub4Truck = new Truck(jsonData.sub4Truck);
      if (jsonData.currentStatus) this.currentStatus = new TruckStatusHistory(jsonData.currentStatus);
      if (jsonData.statusHistory) {
        this.statusHistory = jsonData.statusHistory.map((data) => new TruckStatusHistory(data));
        this.statusHistory = this.statusHistory.sort((e1, e2) => e2.effectiveDate?.diff(e1.effectiveDate) ?? -1);
      }
      if (jsonData.aliasAssignments) {
        this.aliasAssignments = jsonData.aliasAssignments
          .map((data) => {
            return new AliasAssignment(data);
          })
          .sort((e1, e2) => {
            if (e1.toShortString() < e2.toShortString()) return -1;
            if (e1.toShortString() > e2.toShortString()) return 1;
            return 0;
          });
      } else {
        this.aliasAssignments = new Array<AliasAssignment>();
      }
      if (jsonData.assignedAssets) {
        this.assignedAssets = jsonData.assignedAssets
          .map((data) => {
            return new AssetAssignment(data);
          })
          .sort((e1, e2) => {
            if (e1.toShortString() < e2.toShortString()) {
              return -1;
            }
            if (e1.toShortString() > e2.toShortString()) {
              return 1;
            }
            return 0;
          });
        // console.log('Assigned Assets: ', this.assignedAssets);
      } else {
        this.assignedAssets = new Array<AssetAssignment>();
      }
      if (jsonData.expenses) {
        this.expenses = jsonData.expenses
          .map((data) => {
            return new TruckExpense(data);
          })
          .sort((a, b) => {
            if (ExpenseTypeMapping[a.type] > ExpenseTypeMapping[b.type]) return 1;
            if (ExpenseTypeMapping[a.type] < ExpenseTypeMapping[b.type]) return -1;
            return b.amount - a.amount;
          });
      } else {
        this.expenses = new Array<TruckExpense>();
      }
      if (jsonData.valuations) {
        this.valuations = jsonData.valuations
          .map((data) => new TruckValuation(data))
          .sort((a, b) => b.effectiveDate.diff(a.effectiveDate));
        this.currentValuation = this.valuations.find((x) => !x.noData);
        this.lastQuarterValuation = this.valuations.find(
          (x) => !x.noData && x.effectiveDate.isBefore(this.currentValuation.effectiveDate.add(-3, 'month'))
        );
      }
      if (jsonData.files) this.files = jsonData.files.map((data) => new TruckFile(data));
      //Maintenance
      if (jsonData.maintPlan) this.maintPlan = new MaintPlan(jsonData.maintPlan);
      if (jsonData.maintStatuses)
        this.maintStatuses = jsonData.maintStatuses
          .map((data) => {
            const newItem = new MaintStatus(data);
            if (this.currentOdometer) {
              newItem.currentOdometer = this.currentOdometer;
            }
            return newItem;
          })
          .sort((a, b) => (a.maintPlanItem.item.name > b.maintPlanItem.item.name ? 1 : -1));
      if (jsonData.odometerOffsets)
        this.odometerOffsets = jsonData.odometerOffsets.map((data) => new TruckOdometerOffset(data));
    }

    this.statusHistory ??= [];
    this.aliasAssignments ??= [];
    this.assignedAssets ??= [];
    this.expenses ??= [];
    this.maintStatuses ??= [];
    this.valuations ??= [];
    this.files ??= [];
    this.odometerOffsets ??= [];
    if (this.vin) {
      this.year = this.vinDetail?.modelYear ?? this.getVinYear(this.vin);
    }
    this.cabinType ??= TruckCabinType.Unknown;
    this.smokingPreference ??= TruckSmokingPreference.Unknown;
    this.transmission ??= TruckTransmission.Unknown;
  }

  get shortName(): string | undefined {
    return this.toShortString() ?? undefined;
  }

  get defaultSort(): string {
    return this.toShortString();
  }

  get leaseEndDate(): string {
    return this.expenses
      ?.filter((entry) => entry.type === ExpenseType.Lease && entry.endTime !== null)
      .map((entry) => entry.endTime.format('M/D/YY'))
      .join('\n');
  }
  get leaseMaxMiles(): number {
    const response = Math.min(
      ...(this.expenses
        ?.filter((entry) => entry.type === ExpenseType.Lease && entry.restriction !== null && entry.restriction !== 0)
        .map((entry) => entry.restriction) ?? [0])
    );
    //console.log(`leaseMaxMiles = `, response)
    return response === Number.POSITIVE_INFINITY ? null : response;
  }

  get mileageRate(): number {
    return this.expenses
      ?.filter((entry) => entry.type === ExpenseType.Lease && entry.mileageRate !== null)
      .map((entry) => entry.mileageRate)
      .reduce((a, b) => a + b, 0);
  }
  get monthlyRate(): number {
    return this.expenses
      ?.filter((entry) => entry.type === ExpenseType.Lease && entry.amount !== null)
      .map((entry) => entry.amount)
      .reduce((a, b) => a + b, 0);
  }

  get activeAliasAssignments(): AliasAssignment[] {
    const response = this.aliasAssignments?.filter(
      (entry) => !entry.endTime || entry.endTime.isSameOrAfter(SDate.new())
    );
    return response;
  }

  toShortString(): string {
    if (this.company) {
      return `${this.company?.abbreviation}-${this.name}`;
    }
    return this.name ? `${this.name}` : undefined;
  }
  getVinYear(vin: string): number {
    const characterMap = {
      A: 2010,
      B: 2011,
      C: 2012,
      D: 2013,
      E: 2014,
      F: 2015,
      G: 2016,
      H: 2017,
      J: 2018,
      K: 2019,
      L: 2020,
      M: 2021,
      N: 2022,
      P: 2023,
      R: 2024,
      S: 2025,
      T: 2026,
      V: 2027,
      W: 2028,
      X: 2029,
      Y: 2000,
      '1': 2001,
      '2': 2002,
      '3': 2003,
      '4': 2004,
      '5': 2005,
      '6': 2006,
      '7': 2007,
      '8': 2008,
      '9': 2009
    };
    const yearChar = vin[9];
    return characterMap[yearChar];
  }

  getActiveAssignment(date: Dayjs): AliasAssignment {
    return this.aliasAssignments
      .filter(
        (assignment) =>
          assignment.startTime.isSameOrBefore(date) && (!assignment.endTime || assignment.endTime.isSameOrAfter(date))
      )
      .sort((a, b) => -1 * a.startTime.diff(b.startTime))[0];
  }
}
